import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { Config } from 'datatables.net';
import { BsModalService } from 'ngx-bootstrap';
import moment from 'moment';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { DateTimeUtilityService } from 'src/app/core/services/date-time-utility.service';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';
import { ModalConfig } from 'src/app/core/models/modalConfig';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';
import { ReviewReportModalComponent } from './review-report-modal/review-report-modal.component';
import { BOOTSTRAP_PAGE_BUTTON_RENDERER, registerBootstrapPageButtonRenderer } from './bootstrap-page-button-renderer';

declare var $: any;

const CANDIDATES_TYPE_STORAGE_KEY = 'reviewReportsCandidatesTypeVendor';

@Component({
  selector: 'app-review-reports-page',
  templateUrl: './review-reports-page.component.html',
  styleUrls: ['./review-reports-page.component.css']
})
export class ReviewReportsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('startDateInput', { static: true }) startDateInput: ElementRef;
  @ViewChild('endDateInput', { static: true }) endDateInput: ElementRef;

  MESSAGECONSTANTS: any = MESSAGECONSTANTS;
  dtOptions: Config = {};
  assessmentReports: any[] = [];
  companyType: string;
  filterObject: any;
  isLoading = true;

  private defaultStartDate: Date;
  private defaultEndDate: Date;

  constructor(
    private $state: StateService,
    private authService: AuthService,
    private companyService: CompanyService,
    private storageService: StorageService,
    private dateTimeUtilityService: DateTimeUtilityService,
    private alertsAndNotificationsService: AlertAndNotificationsService,
    private bsModalService: BsModalService
  ) { }

  ngOnInit() {
    const userDetails = this.authService.getUserDetails();
    this.companyType = userDetails.company.companyType;
    this.defaultStartDate = moment().subtract(3, 'months').startOf('day').toDate();
    this.defaultEndDate = moment().startOf('day').toDate();

    this.filterObject = {
      companyId: userDetails.company.companyId,
      searchText: '',
      startDate: this._toApiStartDate(this.defaultStartDate),
      endDate: this._toApiEndDate(this.defaultEndDate),
      isVendorCandidates: this._getInitialCandidatesTypeVendor(),
      page: '1',
      size: '50000',
      column: null,
      sortDir: 'ASC'
    };
    this.storageService.set(CANDIDATES_TYPE_STORAGE_KEY, this.filterObject.isVendorCandidates, null);

    this._setDataTable();
    this.getAllCandidatesReportDetails();
  }

  ngAfterViewInit() {
    const datepickerOptions = { format: 'M d, yyyy', weekStart: 1, autoclose: true };
    const $startDate = $(this.startDateInput.nativeElement);
    const $endDate = $(this.endDateInput.nativeElement);

    $startDate.datepicker(datepickerOptions);
    $startDate.datepicker('setDate', this.defaultStartDate);
    $endDate.datepicker({ ...datepickerOptions, startDate: this.defaultStartDate });
    $endDate.datepicker('setDate', this.defaultEndDate);

    // Handlers are bound after setDate so the defaults don't trigger extra fetches.
    $startDate.on('changeDate', (e) => {
      if (e.date) {
        this.filterObject.startDate = this._toApiStartDate(e.date);
        this.getAllCandidatesReportDetails();
      }
    });
    $endDate.on('changeDate', (e) => {
      if (e.date) {
        this.filterObject.endDate = this._toApiEndDate(e.date);
        this.getAllCandidatesReportDetails();
      }
    });
  }

  ngOnDestroy() {
    $(this.startDateInput.nativeElement).datepicker('destroy');
    $(this.endDateInput.nativeElement).datepicker('destroy');
  }

  getAllCandidatesReportDetails() {
    this.isLoading = true;
    this.companyService.getAllCandidatesReportDetails(this.filterObject, (data) => {
      this.assessmentReports = this._massageReportsData(data || []);
      this.isLoading = false;
    }, (error) => {
      this.isLoading = false;
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
    });
  }

  onCandidatesTypeChanged() {
    this.storageService.set(CANDIDATES_TYPE_STORAGE_KEY, this.filterObject.isVendorCandidates, null);
    this.getAllCandidatesReportDetails();
  }

  showReport(report) {
    const config = new ModalConfig();
    config.class = 'custom-ngx-modal custom-modal-65';
    config.initialState = { assessmentInfo: report };
    config.ignoreBackdropClick = true;
    config.backdrop = true;
    this.bsModalService.show(ReviewReportModalComponent, config);
  }

  redirectToUrl(url, isReloadRequired) {
    this.$state.go(url, null, { reload: isReloadRequired });
  }

  private _getInitialCandidatesTypeVendor(): boolean {
    if (this.companyType !== 'Corporation') {
      return false;
    }
    const previousValue = this.storageService.get(CANDIDATES_TYPE_STORAGE_KEY);
    return previousValue === null || previousValue === undefined ? false : previousValue;
  }

  private _toApiStartDate(date: Date) {
    return this.dateTimeUtilityService.getDateTimeInAPIFormat(moment(date).startOf('day').toDate());
  }

  private _toApiEndDate(date: Date) {
    return this.dateTimeUtilityService.getDateTimeInAPIFormat(moment(date).endOf('day').toDate());
  }

  private _massageReportsData(reports: any[]) {
    reports.forEach((report) => {
      report.reportGeneratedDateUTC = moment(report.reportGeneratedDateTime).utc().format('MMM D, YYYY');
      report.reportGeneratedTimeUTC = moment(report.reportGeneratedDateTime).utc().format('h:mm a');
      report.appearedOnDateUTC = moment(report.appearedOn).utc().format('MMM D, YYYY');
      report.appearedOnTimeUTC = moment(report.appearedOn).utc().format('h:mm a');
      const infoList = report.assessmentReportInfoList || [];
      report.violationType = infoList.length > 0 ? infoList[infoList.length - 1].type : '';
    });
    return reports;
  }

  private _setDataTable() {
    registerBootstrapPageButtonRenderer();
    this.dtOptions = {
      pagingType: 'full_numbers',
      order: [[1, 'desc']],
      searching: true,
      lengthChange: false,
      info: true,
      language: { search: '', searchPlaceholder: 'Search', zeroRecords: 'No Reports Available' },
      renderer: BOOTSTRAP_PAGE_BUTTON_RENDERER,
      dom: '<\'row\'<\'col-xs-6\'l><\'col-xs-6\'f>r>t<\'row\'<\'col-xs-6\'i><\'col-xs-6\'p>>',
      oClasses: {
        sWrapper: 'dataTables_wrapper form-inline',
        sFilterInput: 'form-control input-sm'
      },
      columnDefs: [
        { orderable: false, targets: [2, 3, 4, 5, 6, 7, 8, 9] }
      ]
    };
  }
}
